
import React, { useState, useEffect, useRef } from 'react';
import { getBackendService, USE_FIREBASE } from '../../shared/services/apiConfig';
import firestoreService from '../../shared/services/firestoreService';
import { SpinnerIcon } from './icons/SpinnerIcon';
import { UtensilsIcon, TrashIcon, CheckIcon } from './icons/UiIcons';
import { CoverPhotoEditor, DishPhotosEditor, useMountedRef } from './RestaurantPhotosEditor';
import {
    MenuItemPhotoActions,
    MenuItemPhotoField,
    MenuItemThumb,
    MenuPhotoFeedback,
    announceMenuStatus,
    dishLabel,
    isMenuPhotoBusy,
    saveMenuItemWithPhoto,
    useMenuPhotoTasks,
    type MenuPhotoDraft,
} from './MenuItemPhotos';
import { menuItemsNeedingId, mergeSavedItem, newMenuItemId } from '../../shared/menuPhotos';
import {
    normalizeHttpsUrl,
    safeGoogleReviewsUrl,
    safeHttpsUrl,
    validateGoogleReviewsUrl,
    type UrlCheck,
} from '../../shared/restaurantShowcase';
import { userMessage } from '../../shared/userFacingError';
import type { MenuItem, Restaurant } from '../../shared/types';

type LinkField = 'websiteUrl' | 'googleReviewsUrl';

/** Champs facultatifs : vide = pas de lien (champ supprimé à l'enregistrement). */
const checkLink = (field: LinkField, value: string): UrlCheck => {
    if (!value.trim()) return { ok: true, url: '' };
    return field === 'websiteUrl' ? normalizeHttpsUrl(value) : validateGoogleReviewsUrl(value);
};

const infoFromRestaurant = (r: Restaurant) => ({
    name: r.name || '',
    cuisine: r.cuisine || '',
    offer: r.offer || '',
    description: r.description || '',
    address: r.address || '',
    ambiance: r.ambiance || 'Cozy',
    budget: r.budget || 2,
    websiteUrl: r.websiteUrl || '',
    googleReviewsUrl: r.googleReviewsUrl || '',
});

/** Champs gérés par les éditeurs de photos (enregistrés immédiatement, hors formulaire). */
const photoFieldsOf = (r: Restaurant) => ({
    imageUrl: r.imageUrl,
    imageStoragePath: r.imageStoragePath,
    dishPhotos: r.dishPhotos,
});

/**
 * Recharge le restaurant après un enregistrement du formulaire ou de la carte sans
 * toucher aux photos locales : un envoi a pu se terminer entre-temps.
 */
const keepLocalPhotos = (prev: Restaurant | null, fresh: Restaurant): Restaurant =>
    prev ? { ...fresh, ...photoFieldsOf(prev) } : fresh;

const RestaurantProfileEditor: React.FC = () => {
    const [user, setUser] = useState<any>(null);
    const [activeTab, setActiveTab] = useState<'general' | 'menu'>('general');
    const [isLoading, setIsLoading] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [restaurant, setRestaurant] = useState<Restaurant | null>(null);

    const [info, setInfo] = useState({
        name: '', cuisine: '', offer: '', description: '', address: '',
        ambiance: 'Cozy' as any, budget: 2 as any,
        websiteUrl: '', googleReviewsUrl: '',
    });
    const [linkErrors, setLinkErrors] = useState<Partial<Record<LinkField, string>>>({});
    const [menu, setMenu] = useState<MenuItem[]>([]);
    const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
    // Fenêtre plat : nouveau plat ou non (son identifiant est choisi à l'ouverture),
    // photo choisie (envoyée à la confirmation), enregistrement, erreurs.
    const [editingIsNew, setEditingIsNew] = useState(false);
    const [photoDraft, setPhotoDraft] = useState<MenuPhotoDraft>({ kind: 'keep' });
    const [isPreparingPhoto, setIsPreparingPhoto] = useState(false);
    const [isSavingItem, setIsSavingItem] = useState(false);
    const [fieldError, setFieldError] = useState<{ field: 'name' | 'price'; message: string } | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const deletingRef = useRef(false);
    const [isRepairing, setIsRepairing] = useState(false);
    const dialogRef = useRef<HTMLDialogElement>(null);
    const addItemRef = useRef<HTMLButtonElement>(null);
    const menuListRef = useRef<HTMLDivElement>(null);
    // Focus à placer après fermeture de la fenêtre / suppression (undefined : aucun).
    const pendingFocus = useRef<string | null | undefined>(undefined);
    const mounted = useMountedRef();
    const photoTasks = useMenuPhotoTasks();

    useEffect(() => {
        let isMounted = true;
        const load = async () => {
            setIsPageLoading(true);
            try {
                const backend = await getBackendService();
                const currentUser = backend.getCurrentUser();
                if (isMounted) setUser(currentUser);

                const userId = currentUser?.id;
                if (!userId || !currentUser?.restaurantId) {
                    if (isMounted) setIsPageLoading(false);
                    return;
                }

                // Récupérer le restaurant depuis l'API
                const r = await backend.getRestaurant(
                    currentUser.restaurantId
                );

                if (r && isMounted) {
                    setRestaurant(r);
                    setInfo(infoFromRestaurant(r));
                    setMenu(r.menu || []);
                } else if (isMounted && !r) {
                    console.error('Restaurant not found');
                }
            } catch (error: any) {
                console.error('Error loading restaurant:', error);
            } finally {
                if (isMounted) setIsPageLoading(false);
            }
        };

        load();
        return () => { isMounted = false; };
    }, []);

    // Photos tenues à jour à chaque notification du service (envoi terminé après
    // un retour sur la page, autre onglet…). Seuls les champs photo sont repris :
    // une saisie en cours dans le formulaire n'est jamais écrasée.
    useEffect(() => {
        let active = true;
        let unsubscribe: (() => void) | undefined;
        getBackendService().then((backend) => {
            if (!active || !backend.subscribe) return;
            unsubscribe = backend.subscribe(async () => {
                const rId = backend.getCurrentUser()?.restaurantId;
                if (!rId) return;
                const fresh = await backend.getRestaurant(rId);
                if (active && fresh) setRestaurant(prev => (prev ? { ...prev, ...photoFieldsOf(fresh) } : prev));
            });
        });
        return () => {
            active = false;
            unsubscribe?.();
        };
    }, []);

    /** Vérifie un lien en sortie de champ ; retourne la valeur normalisée ou null si invalide. */
    const validateLink = (field: LinkField): string | null => {
        const check = checkLink(field, info[field]);
        if (check.ok === false) {
            setLinkErrors(prev => ({ ...prev, [field]: check.error }));
            return null;
        }
        setLinkErrors(prev => ({ ...prev, [field]: undefined }));
        // Affiche la forme enregistrée (« monresto.fr » → « https://monresto.fr/ »).
        if (check.url !== info[field]) setInfo(prev => ({ ...prev, [field]: check.url }));
        return check.url;
    };

    const renderLinkField = (field: LinkField, label: string, placeholder: string, help: string) => {
        const error = linkErrors[field];
        const preview = field === 'websiteUrl' ? safeHttpsUrl(info[field]) : safeGoogleReviewsUrl(info[field]);
        return (
            <div>
                <label htmlFor={field} className="block text-sm font-bold text-gray-700 mb-2">
                    {label} <span className="font-normal text-gray-400">(facultatif)</span>
                </label>
                <input
                    id={field}
                    type="text"
                    inputMode="url"
                    autoComplete="url"
                    spellCheck={false}
                    value={info[field]}
                    placeholder={placeholder}
                    onChange={e => {
                        setInfo({ ...info, [field]: e.target.value });
                        if (error) setLinkErrors(prev => ({ ...prev, [field]: undefined }));
                    }}
                    onBlur={() => validateLink(field)}
                    aria-invalid={!!error}
                    aria-describedby={`${field}-help`}
                    className={`w-full bg-gray-50 rounded-xl p-3 border focus:border-brand-primary outline-none focus:ring-1 focus:ring-brand-500 ${error ? 'border-red-300' : 'border-gray-400'}`}
                />
                {error ? (
                    <p id={`${field}-help`} role="alert" className="text-sm text-red-600 mt-2">{error}</p>
                ) : (
                    <p id={`${field}-help`} className="text-xs text-gray-400 mt-2">
                        {help}
                        {preview && (
                            <>
                                {' '}
                                <a href={preview} target="_blank" rel="noopener noreferrer" className="font-bold text-brand-primary hover:underline">
                                    Vérifier le lien ↗
                                </a>
                            </>
                        )}
                    </p>
                )}
            </div>
        );
    };

    const handleSaveGeneral = async (e: React.FormEvent) => {
        e.preventDefault();
        const websiteUrl = validateLink('websiteUrl');
        const googleReviewsUrl = validateLink('googleReviewsUrl');
        if (websiteUrl === null || googleReviewsUrl === null) {
            // Focus sur le premier champ fautif (son message d'erreur y est associé).
            document.getElementById(websiteUrl === null ? 'websiteUrl' : 'googleReviewsUrl')?.focus();
            return;
        }
        setIsLoading(true);
        try {
            if (!user?.restaurantId) {
                throw new Error('Restaurant ID manquant');
            }

            const backend = await getBackendService();
            const rId = user.restaurantId;

            // Geocode address → lat/lng via Nominatim (OpenStreetMap, free, no key)
            let geoData: { lat: number; lng: number } | null = null;
            if (info.address && info.address.trim()) {
                try {
                    const geoRes = await fetch(
                        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(info.address)}&limit=1`,
                        { headers: { 'Accept-Language': 'fr' } }
                    );
                    const geoJson = await geoRes.json();
                    if (geoJson?.[0]) {
                        geoData = { lat: parseFloat(geoJson[0].lat), lng: parseFloat(geoJson[0].lon) };
                    }
                } catch (geoErr) { /* geocoding optional */ }
            }

            await backend.updateRestaurantProfile(rId, {
                ...info,
                // Liens normalisés (https://…) ; '' = lien retiré (champ supprimé côté Firestore).
                websiteUrl,
                googleReviewsUrl,
                ...(geoData ? { lat: geoData.lat, lng: geoData.lng } : {}),
            });
            alert("Profil mis à jour avec succès !" + (geoData ? ' Coordonnées mises à jour.' : ''));

            // Recharger les données
            const updated = await backend.getRestaurant(rId);
            if (updated) {
                setRestaurant(prev => keepLocalPhotos(prev, updated));
                setInfo(infoFromRestaurant(updated));
            }
        } catch (error: any) {
            console.error('Error updating restaurant:', error);
            alert(userMessage(error, "Une erreur est survenue lors de la sauvegarde. Vérifiez votre connexion puis réessayez."));
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveMenu = async (menuDataToSave: MenuItem[] = menu, silent = false) => {
        setIsLoading(true);
        try {
            if (!user?.restaurantId) {
                throw new Error('Restaurant ID manquant');
            }

            const backend = await getBackendService();
            const rId = user.restaurantId;
            await backend.updateRestaurantMenu(rId, menuDataToSave);
            if (!silent) alert("La carte a été publiée.");

            // Recharger les données
            const updated = await backend.getRestaurant(rId);
            if (updated) {
                setRestaurant(prev => keepLocalPhotos(prev, updated));
                setMenu(updated.menu || []);
            }
        } catch (error: any) {
            console.error('Error updating menu:', error);
            alert(userMessage(error, "La carte n'a pas pu être publiée. Vérifiez votre connexion puis réessayez."));
        } finally {
            setIsLoading(false);
        }
    };

    /** Après une opération photo : seul le plat concerné est repris de la carte enregistrée. */
    const onMenuPhotoSaved = (saved: MenuItem[], itemId: string) => {
        if (mounted.current) setMenu(prev => mergeSavedItem(prev, saved, itemId));
    };

    /** Focus, après le prochain rendu, sur « Modifier » du plat ou à défaut « Ajouter un plat ». */
    const focusMenuItem = (itemId?: string) => {
        pendingFocus.current = itemId ?? null;
    };
    useEffect(() => {
        if (pendingFocus.current === undefined || editingItem) return;
        const itemId = pendingFocus.current;
        pendingFocus.current = undefined;
        const target = itemId
            ? menuListRef.current?.querySelector<HTMLElement>(`[data-edit-item="${CSS.escape(itemId)}"]`)
            : null;
        (target || addItemRef.current)?.focus();
    }, [menu, editingItem]);

    // Fenêtre modale native : piège à focus, fond inerte, Échap ; défilement de la page verrouillé.
    const dialogOpen = editingItem !== null;
    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialogOpen || !dialog) return;
        if (!dialog.open) dialog.showModal();
        document.getElementById('menu-item-name')?.focus();
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        // Fermeture : l'élément est retiré du document, ce qui le sort de la couche modale.
        return () => { document.body.style.overflow = previousOverflow; };
    }, [dialogOpen]);

    /** Fermeture imposée par le navigateur (Échap répété…) : rouverte pendant l'enregistrement. */
    const onDialogClose = () => {
        const dialog = dialogRef.current;
        if (!dialog?.isConnected) return; // fenêtre déjà retirée par React
        if (isSavingItem) dialog.showModal();
        else closeItemEditor();
    };

    const openItemEditor = (item: MenuItem, isNew: boolean) => {
        setPhotoDraft({ kind: 'keep' });
        setIsPreparingPhoto(false);
        setFieldError(null);
        setSaveError(null);
        setEditingIsNew(isNew);
        setEditingItem(item);
    };

    // Identifiant choisi dès l'ouverture : un enregistrement rejoué ne crée pas de doublon.
    const openNewItem = () => openItemEditor(
        { id: newMenuItemId(), name: '', price: 10, category: 'Plats', available: true },
        true,
    );

    const closeItemEditor = () => {
        if (isSavingItem) return;
        const itemId = editingIsNew ? undefined : editingItem?.id;
        setEditingItem(null);
        setPhotoDraft({ kind: 'keep' });
        focusMenuItem(itemId);
    };

    const editField = (patch: Partial<MenuItem>, field: 'name' | 'price' | null) => {
        if (!editingItem) return;
        setEditingItem({ ...editingItem, ...patch });
        if (field && fieldError?.field === field) setFieldError(null);
    };

    const addOrUpdateMenuItem = async () => {
        if (!editingItem || isSavingItem || isPreparingPhoto) return;
        const name = String(editingItem.name ?? '').trim();
        if (!name) {
            setFieldError({ field: 'name', message: 'Indiquez le nom du plat.' });
            document.getElementById('menu-item-name')?.focus();
            return;
        }
        if (!Number.isFinite(editingItem.price) || editingItem.price < 0) {
            setFieldError({ field: 'price', message: 'Indiquez un prix valide (0 ou plus).' });
            document.getElementById('menu-item-price')?.focus();
            return;
        }
        const item: MenuItem = { ...editingItem, name };
        if (!USE_FIREBASE) {
            // Mode démonstration : carte réécrite en entier, sans photo.
            const newMenu = editingIsNew ? [...menu, item] : menu.map(m => m.id === item.id ? item : m);
            setMenu(newMenu);
            setEditingItem(null);
            await handleSaveMenu(newMenu, true);
            return;
        }
        setIsSavingItem(true);
        setSaveError(null);
        try {
            // Transaction sur ce seul plat (sa photo enregistrée est conservée), puis
            // photo choisie : envoyée même si l'on quitte la page entre-temps.
            await saveMenuItemWithPhoto(user.restaurantId, item, photoDraft, onMenuPhotoSaved);
            if (!mounted.current) return;
            setIsSavingItem(false);
            setEditingItem(null);
            setPhotoDraft({ kind: 'keep' });
            focusMenuItem(item.id);
        } catch (error) {
            if (!mounted.current) return;
            setIsSavingItem(false);
            setSaveError(userMessage(error, "Le plat n'a pas pu être enregistré. Vérifiez votre connexion puis réessayez."));
        }
    };

    const deleteMenuItem = async (item: MenuItem) => {
        if (deletingRef.current) return; // une suppression à la fois (pas de double confirmation)
        if (isMenuPhotoBusy(item.id)) {
            announceMenuStatus(`Attendez la fin de l'envoi de la photo de ${dishLabel(item)} pour supprimer ce plat.`);
            return;
        }
        deletingRef.current = true;
        try {
            if (!window.confirm(`Supprimer ${dishLabel(item)} ?`)) return;
            if (!USE_FIREBASE) {
                const newMenu = menu.filter(m => m.id !== item.id);
                setMenu(newMenu);
                await handleSaveMenu(newMenu, true);
                return;
            }
            // Focus sur le plat suivant (ou « Ajouter un plat ») : la ligne va disparaître.
            const index = menu.findIndex(m => m.id === item.id);
            const neighbour = menu[index + 1] ?? menu[index - 1];
            setDeletingId(item.id);
            // Transaction sur ce seul plat, puis suppression de sa photo dans Storage.
            const saved = await firestoreService.deleteMenuItem(user.restaurantId, item.id);
            if (!mounted.current) return;
            setMenu(prev => mergeSavedItem(prev, saved, item.id));
            focusMenuItem(neighbour?.id);
            announceMenuStatus(`Plat ${dishLabel(item)} supprimé.`);
        } catch (error) {
            alert(userMessage(error, "Le plat n'a pas pu être supprimé. Vérifiez votre connexion puis réessayez."));
        } finally {
            deletingRef.current = false;
            if (mounted.current) setDeletingId(null);
        }
    };

    // Plats sans identifiant (ou en double) : ni modifiables ni supprimables sans risque.
    const needingId = new Set(menuItemsNeedingId(menu));

    const repairMenu = async () => {
        if (isRepairing) return;
        setIsRepairing(true);
        const firstRepaired = Math.min(...needingId);
        try {
            // Identifiants attribués par lots de 20 plats (limite des règles).
            const repaired = await firestoreService.repairMenuItemIds(user.restaurantId);
            if (!mounted.current) return;
            setMenu(repaired);
            focusMenuItem(repaired[firstRepaired]?.id); // le bandeau (et son bouton) disparaît
            announceMenuStatus('Carte réparée : tous les plats peuvent être modifiés.');
        } catch (error) {
            alert(userMessage(error, "La carte n'a pas pu être réparée. Vérifiez votre connexion puis réessayez."));
        } finally {
            if (mounted.current) setIsRepairing(false);
        }
    };

    const menuSaving = isSavingItem || deletingId !== null || isRepairing || Object.keys(photoTasks).length > 0;

    if (isPageLoading) return (
        <div className="h-full flex flex-col items-center justify-center p-12">
            <SpinnerIcon className="w-12 h-12 text-brand-primary mb-4" />
            <p className="text-gray-500 font-medium">Récupération des données établissement...</p>
        </div>
    );

    if (!restaurant) return <div className="p-8 text-center text-red-500 font-bold">Impossible de charger le restaurant.</div>;

    return (
        <div className="max-w-5xl mx-auto pb-20 animate-fade-in">
            <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="page-title">Mon établissement</h1>
                    <p className="page-subtitle">Votre fiche et votre carte, telles que les clients les voient dans l'app.</p>
                </div>
                <div className="flex w-fit flex-shrink-0 bg-cream-200 rounded-full p-1">
                    <button onClick={() => setActiveTab('general')} className={`h-9 px-5 rounded-full text-sm font-semibold transition-all ${activeTab === 'general' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>Informations</button>
                    <button onClick={() => setActiveTab('menu')} className={`h-9 px-5 rounded-full text-sm font-semibold transition-all ${activeTab === 'menu' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>Ma carte</button>
                </div>
            </div>

            {/* Panneau gardé monté (masqué) sur « Ma Carte » : un envoi de photos en cours continue normalement. */}
            <div hidden={activeTab !== 'general'}>
                <div className="space-y-6">
                {USE_FIREBASE ? (
                    <CoverPhotoEditor
                        restaurantId={user.restaurantId}
                        imageUrl={restaurant.imageUrl}
                        onChange={cover => setRestaurant(prev => (prev ? { ...prev, ...cover } : prev))}
                    />
                ) : (
                    <p className="text-sm text-gray-500 bg-white p-4 rounded-2xl border border-gray-100">
                        Les photos de l'établissement ne sont pas disponibles en mode démonstration.
                    </p>
                )}
                <form onSubmit={handleSaveGeneral} className="bg-white p-6 sm:p-8 rounded-3xl border border-black/[0.08] space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-bold text-gray-700 mb-2">Nom du restaurant</label>
                            <input required type="text" value={info.name} onChange={e => setInfo({ ...info, name: e.target.value })} className="w-full bg-gray-50 border-gray-400 rounded-xl p-3 border focus:border-brand-primary outline-none focus:ring-1 focus:ring-brand-500" />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Cuisine</label>
                            <input required type="text" value={info.cuisine} onChange={e => setInfo({ ...info, cuisine: e.target.value })} className="w-full bg-gray-50 border-gray-400 rounded-xl p-3 border focus:border-brand-primary outline-none focus:ring-1 focus:ring-brand-500" />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Offre de bienvenue</label>
                            <input required type="text" value={info.offer} onChange={e => setInfo({ ...info, offer: e.target.value })} className="w-full bg-gray-50 border-gray-400 rounded-xl p-3 border focus:border-brand-primary outline-none focus:ring-1 focus:ring-brand-500" />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-bold text-gray-700 mb-2">Adresse</label>
                            <input type="text" value={info.address} onChange={e => setInfo({ ...info, address: e.target.value })} placeholder="12 Rue de la Paix, 75001 Paris" className="w-full bg-gray-50 border-gray-400 rounded-xl p-3 border focus:border-brand-primary outline-none focus:ring-1 focus:ring-brand-500" />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Ambiance</label>
                            <select value={info.ambiance} onChange={e => setInfo({ ...info, ambiance: e.target.value as any })} className="w-full bg-gray-50 border-gray-400 rounded-xl p-3 border focus:border-brand-primary outline-none focus:ring-1 focus:ring-brand-500">
                                <option>Cozy</option><option>Festif</option><option>Romantique</option><option>Business</option><option>Chill</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Budget moyen</label>
                            <select value={info.budget} onChange={e => setInfo({ ...info, budget: parseInt(e.target.value) as any })} className="w-full bg-gray-50 border-gray-400 rounded-xl p-3 border focus:border-brand-primary outline-none focus:ring-1 focus:ring-brand-500">
                                <option value="1">€</option><option value="2">€€</option><option value="3">€€€</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Description</label>
                        <textarea rows={4} value={info.description} onChange={e => setInfo({ ...info, description: e.target.value })} className="w-full bg-gray-50 border-gray-400 rounded-xl p-3 border focus:border-brand-primary outline-none focus:ring-1 focus:ring-brand-500" />
                    </div>
                    <div className="border-t border-gray-100 pt-6 space-y-6">
                        <div>
                            <h2 className="text-lg font-bold text-gray-800">Liens</h2>
                            <p className="text-sm text-gray-500">Bientôt visibles sur votre fiche dans l'app atable. Liens sécurisés (https://) uniquement.</p>
                        </div>
                        {renderLinkField(
                            'websiteUrl',
                            'Site internet',
                            'https://www.monrestaurant.fr',
                            'L\'adresse de votre site, de votre page de réservation ou de votre carte en ligne.',
                        )}
                        {renderLinkField(
                            'googleReviewsUrl',
                            'Lien vers vos avis Google',
                            'https://maps.app.goo.gl/…',
                            'Sur Google Maps, ouvrez la fiche de votre restaurant, touchez « Partager » puis « Copier le lien ». Vos clients pourront bientôt consulter vos avis depuis l\'app (bouton « Voir nos avis Google »).',
                        )}
                    </div>
                    <div className="flex justify-end pt-4">
                        <button type="submit" disabled={isLoading} className="px-8 py-3 bg-brand-primary text-white font-bold rounded-lg flex items-center min-w-[140px] justify-center transition-all active:scale-95">
                            {isLoading ? <SpinnerIcon className="mr-0" /> : "Enregistrer"}
                        </button>
                    </div>
                </form>
                {USE_FIREBASE && (
                    <DishPhotosEditor
                        restaurantId={user.restaurantId}
                        photos={restaurant.dishPhotos ?? []}
                        onChange={photos => setRestaurant(prev => (prev ? { ...prev, dishPhotos: photos } : prev))}
                    />
                )}
                </div>
            </div>
            {activeTab === 'menu' && (
                <div className="space-y-6">
                    <div className="bg-white p-6 rounded-3xl border border-black/[0.08] flex justify-between items-center">
                        <div>
                            <p className="text-gray-600 font-medium">Gérez les plats disponibles sur votre profil client.</p>
                            {USE_FIREBASE && (
                                <p className="text-sm text-gray-400">Ajoutez une photo à chaque plat : vos clients la voient dans l'app atable. Enregistrée dès l'envoi.</p>
                            )}
                        </div>
                        <button ref={addItemRef} onClick={openNewItem} className="h-10 px-4 bg-brand-500 text-cream text-sm font-medium rounded-lg hover:bg-brand-600 active:translate-y-px">+ Ajouter un plat</button>
                    </div>

                    {USE_FIREBASE && needingId.size > 0 && (
                        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3">
                            <p className="text-sm text-amber-800">
                                {needingId.size > 1 ? `${needingId.size} plats n'ont pas d'identifiant` : "Un plat n'a pas d'identifiant"} (carte
                                créée avec une ancienne version) : impossible de les modifier ou d'y ajouter une photo tant que la carte n'est pas réparée.
                            </p>
                            <button
                                type="button"
                                onClick={repairMenu}
                                aria-disabled={isRepairing}
                                className="px-4 py-2 bg-brand-primary text-white text-sm font-bold rounded-lg shadow inline-flex items-center gap-2 aria-disabled:opacity-60"
                            >
                                {isRepairing && <SpinnerIcon className="w-4 h-4" />}
                                Réparer la carte
                            </button>
                        </div>
                    )}

                    {USE_FIREBASE && <MenuPhotoFeedback menu={menu} />}

                    <div ref={menuListRef} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {menu.map((item, index) => {
                            const editable = !needingId.has(index);
                            const busy = editable ? photoTasks[item.id] : undefined;
                            const name = dishLabel(item);
                            return (
                                <div key={editable ? item.id : `a-reparer-${index}`} className="bg-white p-4 rounded-2xl border border-gray-100 flex items-center justify-between group hover:shadow-md transition-all">
                                    <div className="flex items-center space-x-4 min-w-0">
                                        {USE_FIREBASE && editable ? (
                                            <MenuItemThumb restaurantId={user.restaurantId} item={item} />
                                        ) : (
                                            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 rounded-lg flex items-center justify-center shadow-inner"><UtensilsIcon className="w-5 h-5" /></div>
                                        )}
                                        <div className="min-w-0">
                                            <p className="font-bold text-gray-800">{item.name}</p>
                                            <p className="text-sm text-gray-400">{item.category} • {typeof item.price === 'number' ? `${item.price.toFixed(2)}€` : '—'}</p>
                                            {USE_FIREBASE && editable && (
                                                <MenuItemPhotoActions restaurantId={user.restaurantId} item={item} onSaved={onMenuPhotoSaved} />
                                            )}
                                            {!editable && <p className="text-xs font-bold text-amber-700 mt-1">À réparer</p>}
                                        </div>
                                    </div>
                                    {editable && (
                                        <div className="flex space-x-2 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                                            <button onClick={() => openItemEditor(item, false)} data-edit-item={item.id} aria-label={`Modifier ${name}`} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg">✏️</button>
                                            <button
                                                onClick={() => deleteMenuItem(item)}
                                                aria-disabled={busy !== undefined || deletingId !== null}
                                                aria-label={`Supprimer ${name}`}
                                                aria-describedby={busy ? `menu-busy-${index}` : undefined}
                                                className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors aria-disabled:opacity-40 aria-disabled:cursor-not-allowed"
                                            >
                                                {deletingId === item.id ? <SpinnerIcon className="w-4 h-4" /> : <TrashIcon className="w-4 h-4" />}
                                            </button>
                                            {busy && (
                                                <span id={`menu-busy-${index}`} className="sr-only">
                                                    Opération en cours sur ce plat : suppression possible ensuite.
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className="flex justify-end pt-6">
                        {USE_FIREBASE ? (
                            // Chaque plat est enregistré dès sa modification : plus de publication
                            // globale, qui pouvait écraser des plats ajoutés depuis un autre onglet.
                            <p className="text-sm text-gray-500 inline-flex items-center gap-2">
                                {menuSaving ? (
                                    <><SpinnerIcon className="w-4 h-4" /> Enregistrement en cours…</>
                                ) : (
                                    <><CheckIcon className="w-4 h-4 text-green-600" /> Toutes vos modifications sont enregistrées automatiquement.</>
                                )}
                            </p>
                        ) : (
                            <button onClick={() => handleSaveMenu()} disabled={isLoading} className="px-8 py-3 bg-brand-primary text-white font-bold rounded-lg flex items-center min-w-[160px] justify-center transition-all active:scale-95">
                                {isLoading ? <SpinnerIcon className="mr-0" /> : "Publier la carte"}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Fenêtre plat : <dialog> natif ouvert par showModal() (cf. effet dialogOpen). */}
            {editingItem && (
                <dialog
                    ref={dialogRef}
                    aria-labelledby="menu-item-dialog-title"
                    onCancel={e => { e.preventDefault(); closeItemEditor(); }}
                    onClose={onDialogClose}
                    className="p-0 border-0 bg-transparent w-full max-w-md overflow-visible backdrop:bg-black/60 backdrop:backdrop-blur-sm"
                >
                    <form
                        noValidate
                        onSubmit={e => { e.preventDefault(); void addOrUpdateMenuItem(); }}
                        className="bg-white rounded-2xl shadow-2xl p-6 animate-fade-in-up max-h-[90vh] overflow-y-auto"
                    >
                        <h2 id="menu-item-dialog-title" className="text-xl font-bold mb-6 text-gray-800">{editingIsNew ? 'Nouveau plat' : 'Modifier le plat'}</h2>
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="menu-item-name" className="block text-xs font-bold text-gray-400 mb-1">Nom du plat</label>
                                <input
                                    id="menu-item-name"
                                    type="text"
                                    required
                                    value={editingItem.name ?? ''}
                                    onChange={e => editField({ name: e.target.value }, 'name')}
                                    aria-invalid={fieldError?.field === 'name'}
                                    aria-describedby={fieldError?.field === 'name' ? 'menu-item-name-error' : undefined}
                                    className={`w-full p-3 bg-gray-50 border rounded-xl outline-none focus:ring-2 focus:ring-brand-primary ${fieldError?.field === 'name' ? 'border-red-300' : ''}`}
                                />
                                {fieldError?.field === 'name' && <p id="menu-item-name-error" role="alert" className="text-sm text-red-600 mt-1">{fieldError.message}</p>}
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="menu-item-price" className="block text-xs font-bold text-gray-400 mb-1">Prix (€)</label>
                                    <input
                                        id="menu-item-price"
                                        type="number"
                                        step="0.5"
                                        min="0"
                                        required
                                        value={Number.isFinite(editingItem.price) ? editingItem.price : ''}
                                        onChange={e => editField({ price: parseFloat(e.target.value) }, 'price')}
                                        aria-invalid={fieldError?.field === 'price'}
                                        aria-describedby={fieldError?.field === 'price' ? 'menu-item-price-error' : undefined}
                                        className={`w-full p-3 bg-gray-50 border rounded-xl outline-none focus:ring-2 focus:ring-brand-primary ${fieldError?.field === 'price' ? 'border-red-300' : ''}`}
                                    />
                                    {fieldError?.field === 'price' && <p id="menu-item-price-error" role="alert" className="text-sm text-red-600 mt-1">{fieldError.message}</p>}
                                </div>
                                <div>
                                    <label htmlFor="menu-item-category" className="block text-xs font-bold text-gray-400 mb-1">Catégorie</label>
                                    <select id="menu-item-category" value={editingItem.category} onChange={e => editField({ category: e.target.value }, null)} className="w-full p-3 bg-gray-50 border rounded-xl outline-none focus:ring-2 focus:ring-brand-primary">
                                        <option>Plats</option><option>Entrées</option><option>Desserts</option><option>Boissons</option>
                                    </select>
                                </div>
                            </div>
                            {USE_FIREBASE && (
                                <MenuItemPhotoField
                                    restaurantId={user.restaurantId}
                                    item={menu.find(m => m.id === editingItem.id) ?? editingItem}
                                    draft={photoDraft}
                                    onDraftChange={setPhotoDraft}
                                    onPreparingChange={setIsPreparingPhoto}
                                    disabled={isSavingItem}
                                />
                            )}
                            {saveError && <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3">{saveError}</p>}
                        </div>
                        <div className="flex space-x-3 mt-8">
                            <button type="button" onClick={closeItemEditor} aria-disabled={isSavingItem} className="flex-1 py-3 font-bold text-gray-500 hover:text-gray-800 aria-disabled:opacity-50">Annuler</button>
                            <button type="submit" aria-disabled={isSavingItem || isPreparingPhoto} className="flex-1 py-3 bg-brand-primary text-white font-bold rounded-lg active:scale-95 transition-transform flex items-center justify-center aria-disabled:opacity-70">
                                {isSavingItem ? <><SpinnerIcon className="w-4 h-4" /><span className="sr-only">Enregistrement…</span></> : 'Confirmer'}
                            </button>
                        </div>
                    </form>
                </dialog>
            )}
        </div>
    );
};
export default RestaurantProfileEditor;
